import { useCallback, useEffect, useMemo, useState } from "react"

import { useForm } from "@mantine/form"
import dayjs, { Dayjs } from "dayjs"

import useParseSchedule, { WittyPiSchedule } from "../../hooks/useParseSchedule"
import schedulePresets from "./schedulePresets"
import { useCloudFunction } from "../../../../hooks/useCloudFunction"
import { httpsCallable } from "firebase/functions"
import { AwsLambdaUpdateThingData } from "../../../../functions/aws/lambda/aws-lambda-updateThing"
import { firebase } from "../../../../services/firebase/firebase"
import { useDisclosure } from "@mantine/hooks"
import { WittyPiScheduleAttribute } from "./OperatingTimeModuleSettings"
import { AwsLambdaUpdateGroupData } from "../../../../functions/aws/lambda/aws-lambda-updateGroup"
import { useLanguage } from "../../../../contexts/language-context"
import isEqual from "lodash.isequal"
import { Attribute, DeviceDocument } from "../../../../contexts/devices-context"

type OperatingTimeSettings = WittyPiSchedule & {
    preset: string | null
}

export default (
    attrs: Record<"schedule", WittyPiScheduleAttribute> | undefined,
    organisation: string,
    group: string,
    device?: DeviceDocument,
    coordinates?: {latitude: number, longitude: number}
) => {
    const [times, setTimes] = useState<[Dayjs, Dayjs]>()
    const [loading, setLoading] = useState(true)
    const { onStart, onSuccess, onError, processing } = useCloudFunction("aws-lambda-updateThing")
    const { parse, stringify } = useParseSchedule()
    const { L } = useLanguage()
    const preset = useMemo(() => schedulePresets(L).find(preset => preset.value === attrs?.schedule.value), [attrs])
    const parsedLines = useMemo(
        () =>
            typeof attrs?.schedule.value === "object" && attrs.schedule.value !== null
                ? parse(attrs.schedule.value.inputs)
                : null,
        [attrs, parse],
    )
    const form = useForm<OperatingTimeSettings>({
        initialValues: preset
            ? {
                  preset: preset.value,
                  lines: preset.lines,
                  dayPattern: preset.dayPattern ?? [true, true, true, true, true, true, true],
              }
            : parsedLines
            ? {
                  preset: "custom",
                  lines: parsedLines,
                  dayPattern:
                      typeof attrs?.schedule.value === "object" && attrs.schedule.value !== null
                          ? attrs.schedule.value.day_pattern
                          : [true, true, true, true, true, true, true],
              }
            : { preset: null, lines: [], dayPattern: [true, true, true, true, true, true, true] },
        validate: {
            lines: value =>
                value.some(line => isEqual(line, {}))
                    ? L("Time schedule rules cannot be empty. Please remove or update empty rules.")
                    : null,
        },
    })
    const [dayPatternIsComplete, { toggle: _toggleDayPattern, open: openDayPattern, close: closeDayPattern }] =
        useDisclosure(form.values.dayPattern?.every(Boolean))
    const addRule = useCallback(() => form.insertListItem("lines", {}), [form])
    const removeRule = useCallback((index: number) => () => form.removeListItem("lines", index), [form])

    useEffect(() => {
        if (coordinates) {
            fetch(`https://api.sunrise-sunset.org/json?lat=${coordinates?.latitude ?? 52.083843}&lng=${coordinates?.longitude ?? 5.834269}&formatted=0`)
                .then(response => {
                    if (!response.ok) throw new Error(`Could not fetch Sunrise-Sunset API (${response.status})`)
                    return response.json()
                })
                .then(data => setTimes([dayjs(data.results.sunrise), dayjs(data.results.sunset)]))
                .finally(() => setLoading(false))
                .catch(console.error)
        } else {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        if (form.values.preset !== null && form.values.preset !== "custom") {
            const newPreset = schedulePresets(L).find(_preset => _preset.value === form.values.preset)
            form.setFieldValue("lines", newPreset?.lines ?? [])
        }
    }, [form.values.preset])

    const onSubmit = async (values: typeof form.values) => {
        onStart(L("Updating setting..."))

        const module = {
            name: "wittypi",
            attributes: [
                {
                    name: "schedule",
                    value:
                        values.preset === null
                            ? null
                            : values.preset !== "custom"
                            ? values.preset
                            : {
                                  name: (Math.random() + 1).toString(36).substring(2, 8),
                                  inputs: stringify(values.lines),
                                  day_pattern: values.dayPattern,
                              },
                },
            ] as Attribute[],
        }

        try {
            if (device) {
                await httpsCallable<AwsLambdaUpdateThingData>(
                    firebase.functions,
                    "aws-lambda-updateThing",
                )({
                    id: device.id,
                    group,
                    organisation,
                    module,
                })
            } else {
                await httpsCallable<AwsLambdaUpdateGroupData>(
                    firebase.functions,
                    "aws-lambda-updateGroup",
                )({
                    id: group,
                    organisation,
                    module,
                })
            }

            form.resetDirty(values)
            onSuccess()()
        } catch (error) {
            onError()(error)
        }
    }

    return {
        form,
        onSubmit,
        processing,
        reset() {
            form.reset()
            if (form.values.dayPattern?.every(Boolean)) openDayPattern()
            else closeDayPattern()
        },
        inheritSettings: () => form.setValues({ preset: null }),
        times,
        loading,
        dayPatternIsComplete,
        toggleDayPattern() {
            _toggleDayPattern()
            form.setFieldValue("dayPattern", [true, true, true, true, true, true, true])
        },
        addRule,
        removeRule,
    }
}
